/**
 * messageReply — WHICH EARLIER MESSAGE a thread message answers (client twin; the
 * "reply to a specific message" tooling, Miles 2026-09-15).
 *
 * A reply is a message like any other, plus ONE map on it, `replyTo`:
 *   • `messageId`          — the target's SHARED anchor, the id every lane
 *                             already agrees on (the same id reactions key on):
 *                             `prest0n_task_<id>` for one of Prest0n's answers,
 *                             `prest0n_task_<id>__user` for a person's turn, or
 *                             a summon-suppressed post's own id.
 *   • `senderId`,
 *     `senderDisplayName`  — WHO wrote the target, as the STORE says;
 *   • `excerpt`            — the target's opening words, bounded, so a reader
 *                             (and Prest0n) can see what is being answered
 *                             without a lookup — a reader below their history
 *                             floor could not resolve the id at all;
 *   • `orderTs`            — the target's instant;
 *   • `verified`           — true when the SERVER re-derived sender + excerpt
 *                             from the shared store. A quote of a message the
 *                             thread does not hold is never written for other
 *                             people; the sender's own optimistic copy carries
 *                             their client's ref until the echo arrives.
 *
 * ABSENT ⇒ TODAY'S BEHAVIOUR, BYTE FOR BYTE: every message ever written carries
 * no `replyTo`, every reader here returns null for it, every transcript entry
 * renders exactly as it did.
 *
 * VERBATIM TWIN of functions/src/prest0nSession/messageReply.ts (separate tsconfig roots — the
 * messageAuthorship.ts / groupThreadMessageDoc.ts idiom). Behavioural parity
 * is pinned by tests/prest0nSession/messageReply.parity.test.ts.
 *
 * PURE: no React, no Firebase, no clock, no imports.
 */

/** The one field name. A nested map so the facts are written and read as one. */
export const REPLY_TO_FIELD = 'replyTo' as const;

/** The excerpt's ceiling — long enough to recognise the message, never a second copy of it. */
export const REPLY_EXCERPT_MAX_CHARS = 160;
/** Bounds on the identity strings; anything longer is not a message id or a name. */
export const REPLY_MESSAGE_ID_MAX_CHARS = 200;
export const REPLY_NAME_MAX_CHARS = 120;

/**
 * The sentinel senderId Prest0n's own messages carry in the shared store —
 * restated here so this module stays a pure leaf (groupThreadMessageDoc's
 * ASSISTANT_SENDER_ID is the source; the parity test pins them equal).
 */
export const REPLY_ASSISTANT_SENDER_ID = 'assistant' as const;

export interface MessageReplyRef {
  /** The target's shared anchor id (see the header). */
  messageId: string;
  /** Who wrote the target: a person's uid, or REPLY_ASSISTANT_SENDER_ID. */
  senderId?: string;
  /** The target author's display name as stamped on the target. */
  senderDisplayName?: string;
  /** The target's opening words (replyExcerpt); '' for an attachment-only target. */
  excerpt?: string;
  /** The target's sort instant (epoch ms). */
  orderTs?: number;
  /** True when the server re-derived sender + excerpt from the shared store. */
  verified?: boolean;
}

function boundedString(v: unknown, max: number): string {
  if (typeof v !== 'string') return '';
  const s = v.trim();
  if (!s) return '';
  return s.length > max ? s.slice(0, max) : s;
}

/**
 * The target's opening words: whitespace collapsed, bounded to
 * REPLY_EXCERPT_MAX_CHARS with a single ellipsis when cut. Never invents a
 * word — a verbatim excerpt of what someone already wrote (the push-body
 * excerpt's own rule).
 */
export function replyExcerpt(text: unknown): string {
  if (typeof text !== 'string') return '';
  const collapsed = text.replace(/\s+/g, ' ').trim();
  if (!collapsed) return '';
  if (collapsed.length <= REPLY_EXCERPT_MAX_CHARS) return collapsed;
  return `${collapsed.slice(0, REPLY_EXCERPT_MAX_CHARS - 1).trimEnd()}…`;
}

/**
 * Parse an untrusted `replyTo` value, fail-closed: null unless it is an
 * object with a non-empty string `messageId`. Every string is trimmed and
 * bounded; unknown keys are dropped; `orderTs` must be a finite positive
 * number; `verified` is kept only when it is literally `true`.
 */
export function normalizeReplyRef(raw: unknown): MessageReplyRef | null {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null;
  const r = raw as Record<string, unknown>;
  const messageId = boundedString(r.messageId, REPLY_MESSAGE_ID_MAX_CHARS);
  if (!messageId) return null;
  const out: MessageReplyRef = { messageId };
  const senderId = boundedString(r.senderId, REPLY_MESSAGE_ID_MAX_CHARS);
  if (senderId) out.senderId = senderId;
  const senderDisplayName = boundedString(r.senderDisplayName, REPLY_NAME_MAX_CHARS);
  if (senderDisplayName) out.senderDisplayName = senderDisplayName;
  if (typeof r.excerpt === 'string') {
    const excerpt = replyExcerpt(r.excerpt);
    if (excerpt) out.excerpt = excerpt;
  }
  if (typeof r.orderTs === 'number' && Number.isFinite(r.orderTs) && r.orderTs > 0) {
    out.orderTs = Math.floor(r.orderTs);
  }
  if (r.verified === true) out.verified = true;
  return out;
}

/**
 * Whose message is being answered, as a name. The chain is the one the
 * transcript already uses for speakers: the caller's speaker labels (uid →
 * name), then the name stamped on the target, then Prest0n by sentinel, then
 * "a teammate" — never a raw uid.
 */
export function replyTargetLabel(
  ref: Pick<MessageReplyRef, 'senderId' | 'senderDisplayName'> | null | undefined,
  speakerLabels?: Record<string, string> | null,
): string {
  const senderId = (ref?.senderId ?? '').trim();
  if (senderId && speakerLabels) {
    const labelled = String(speakerLabels[senderId] || '').trim();
    if (labelled) return labelled;
  }
  const stamped = (ref?.senderDisplayName ?? '').trim();
  if (stamped) return stamped;
  if (senderId === REPLY_ASSISTANT_SENDER_ID) return 'Prest0n';
  return 'a teammate';
}

/**
 * The internal bracket a reply carries into the model's transcript, appended
 * to the entry like every other bracket (reactions, seen-by, delegation).
 * Deterministic and code-authored on purpose — the D9 structural class: it
 * states a fact about the thread (which message this answers), never content.
 */
export function replyTranscriptBracket(targetLabel: string, excerpt: string): string {
  const who = (targetLabel || '').trim() || 'a teammate';
  const quote = (excerpt || '').trim();
  return quote
    ? `[in reply to ${who}'s message: "${quote}"]`
    : `[in reply to ${who}'s earlier message]`;
}
